/**
 * YouTubeOAuthButton — shows the current OAuth connection state and lets the
 * user connect or disconnect their YouTube channel.
 *
 * States:
 *   loading         → null — request in-flight
 *   fetch-error     → fetchFailed=true — backend unreachable, retry available
 *   not-configured  → configured=false — credentials absent, no action possible
 *   no-profile      → "Connect YouTube" button
 *   authing         → "Waiting for browser..." spinner button (disabled)
 *   connected       → avatar + channel title + "Sign out" button
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { isOAuthConfigured, logoutOAuth, listProfiles } from '../../services/youtubePublishApi';
import type { YouTubeProfile } from '../../services/youtubePublishApi';
import { startOAuthFlow } from '../../services/youtubeOAuthLauncher';

interface YouTubeOAuthButtonProps {
  /** Called with the connected profile (or null after logout) so parent can
   *  react without doing a separate fetch. */
  onProfileChange?: (profile: YouTubeProfile | null) => void;
}

export function YouTubeOAuthButton({ onProfileChange }: YouTubeOAuthButtonProps) {
  const { t } = useTranslation();
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [profile, setProfile] = useState<YouTubeProfile | null>(null);
  const [isAuthing, setIsAuthing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Init: check capability + load existing profile
  // -------------------------------------------------------------------------
  const refresh = useCallback(async () => {
    setFetchFailed(false);
    setIsConfigured(null);
    try {
      const configured = await isOAuthConfigured();
      setIsConfigured(configured);
      if (configured) {
        const profiles = await listProfiles();
        const current = profiles.length > 0 ? profiles[0] : null;
        setProfile(current);
        onProfileChange?.(current);
      }
    } catch {
      // Network/backend error — distinct from "credentials not configured".
      // Show a retry button instead of hiding the feature permanently.
      setFetchFailed(true);
      setIsConfigured(false);
    }
  }, [onProfileChange]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // -------------------------------------------------------------------------
  // Connect
  // -------------------------------------------------------------------------
  const handleConnect = useCallback(async () => {
    setError(null);
    setIsAuthing(true);
    try {
      const result = await startOAuthFlow();
      // Web flow does a full-page redirect; control never returns here.
      // Desktop returns the OAuthStatusResponse once polling completes.
      if (result === undefined) return;
      if (result.status === 'completed') {
        await refresh();
      } else {
        setError(result.error ?? 'OAuth failed — please try again.');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'OAuth failed — please try again.');
    } finally {
      setIsAuthing(false);
    }
  }, [refresh]);

  // -------------------------------------------------------------------------
  // Disconnect
  // -------------------------------------------------------------------------
  const handleLogout = useCallback(async () => {
    if (!profile) return;
    try {
      await logoutOAuth(profile.channel_id);
      setProfile(null);
      onProfileChange?.(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Logout failed.');
    }
  }, [profile, onProfileChange]);

  // -------------------------------------------------------------------------
  // Render: still loading
  // -------------------------------------------------------------------------
  if (isConfigured === null) {
    return (
      <div className="flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-lg bg-black/30 border border-white/10">
        <span className="text-white/50 text-sm">{t('common.loading')}</span>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: fetch failed (backend unreachable — transient, show retry)
  // -------------------------------------------------------------------------
  if (fetchFailed) {
    return (
      <div className="flex items-center gap-3 min-h-[44px] px-4 py-2 rounded-lg bg-black/30 border border-white/10">
        <span className="text-white/50 text-sm">{t('errors.generic')}</span>
        <button
          onClick={() => void refresh()}
          className="text-purple-400 hover:text-purple-300 text-sm underline transition-colors"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: OAuth credentials not configured
  // -------------------------------------------------------------------------
  if (!isConfigured) {
    return (
      <div className="flex items-center gap-2 min-h-[44px] px-4 py-2 rounded-lg bg-black/30 border border-white/10">
        <span className="text-white/50 text-sm">{t('youtube.oauth.notConfigured')}</span>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: connected
  // -------------------------------------------------------------------------
  if (profile) {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center gap-3 bg-black/30 backdrop-blur-xl border border-white/10 rounded-lg px-4 py-2 min-h-[44px]">
          <div className="flex flex-col">
            <span className="text-white text-sm font-medium">
              {t('youtube.oauth.connected', { channel: profile.channel_title })}
            </span>
            <span className="text-white/50 text-xs">{profile.email}</span>
          </div>
        </div>
        <button
          onClick={() => void handleLogout()}
          className="min-h-[44px] px-4 py-2 rounded-lg bg-black/30 backdrop-blur-xl border border-white/10 text-white/70 hover:text-white hover:border-white/20 text-sm transition-colors"
        >
          {t('youtube.oauth.logout')}
        </button>
        {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render: not connected
  // -------------------------------------------------------------------------
  return (
    <div className="flex flex-col gap-2">
      <button
        onClick={() => void handleConnect()}
        disabled={isAuthing}
        className="min-h-[44px] px-4 py-2 rounded-lg bg-purple-600/80 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-xl border border-purple-500/30 text-white text-sm font-medium transition-colors"
      >
        {isAuthing ? t('youtube.oauth.connecting') : t('youtube.oauth.connect')}
      </button>
      {error && <p className="text-red-400 text-xs">{error}</p>}
    </div>
  );
}
